import { FluidPaginationState, FluidPaginatorTokens } from '@lmig/fluid-core';

/**
 * Default wording
 */
export const DEFAULT_RANGE_INDICATOR = () => {
  const { RANGE_FROM, RANGE_TO, TOTAL_ITEMS } = FluidPaginatorTokens;
  return `${RANGE_FROM} - ${RANGE_TO} of ${TOTAL_ITEMS}`;
};
export const DEFAULT_ITEMS_PER_PAGE = 'Items per page';
/**
 * Gets default page size options
 */
export const getDefaultPageSizeOptions = () => [5, 10, 25, 50, 100];

/**
 * Gets a default pagination state from the given data
 * @param dataItems
 * @param defaultPageSize
 * @param currentPage
 */
export const getDefaultPaginationState = (
  dataItems: object[],
  defaultPageSize: number,
  currentPage: number
): FluidPaginationState => ({
  totalPages: Math.ceil(dataItems.length / defaultPageSize),
  itemsPerPage: defaultPageSize,
  currentPage,
  rangeFrom: 1,
  rangeTo: defaultPageSize,
});

/**
 * Calculate total pages based on the total number of
 * items divided by items per page and rounded.
 * @param dataItems
 * @param itemsPerPage
 */
export const calculateTotalPages = (
  dataItems: any[],
  itemsPerPage: number
): number => {
  return Math.ceil(dataItems.length / itemsPerPage);
};

/**
 * Calculate the starting item for range indicator
 * @param currentPage
 * @param itemsPerPage
 */
export const calculateRangeFrom = (
  currentPage: number,
  itemsPerPage: number
): number => {
  return currentPage * itemsPerPage + 1;
};

/**
 * Calculate the ending item for range indicator
 * @param currentPage
 * @param totalPages
 * @param dataItems
 * @param itemsPerPage
 */
export const calculateRangeTo = (
  currentPage: number,
  totalPages: number,
  dataItems: any[],
  itemsPerPage: number
) => {
  return currentPage === totalPages - 1
    ? dataItems.length
    : (currentPage + 1) * itemsPerPage;
};

/**
 * Calculate the maximum current page after update. If current page is less than the max last page, return it,
 * otherwise, return the maximum current page for recalculation.
 * @param currentPage
 * @param totalPages
 */
export const calculateMaxCurrentPage = (
  currentPage: number,
  totalPages: number
): number => {
  return currentPage > totalPages - 1 ? totalPages - 1 : currentPage;
};

/**
 * Use the wording template, along with pagination state and total items to provide the wording
 * for range indicator
 * @param paginationState
 * @param totalItems
 * @param template
 */
export const getRangeIndicatorWording = (
  paginationState: FluidPaginationState,
  totalItems: number,
  template: string
) => {
  const {
    TOTAL_ITEMS,
    TOTAL_PAGES,
    RANGE_TO,
    ITEMS_PER_PAGE,
    CURRENT_PAGE,
    RANGE_FROM,
  } = FluidPaginatorTokens;
  return template
    .replace(
      new RegExp(TOTAL_PAGES, 'g'),
      paginationState.totalPages?.toString()
    )
    .replace(new RegExp(RANGE_FROM, 'g'), paginationState.rangeFrom?.toString())
    .replace(new RegExp(RANGE_TO, 'g'), paginationState.rangeTo?.toString())
    .replace(
      new RegExp(CURRENT_PAGE, 'g'),
      (paginationState.currentPage + 1).toString()
    )
    .replace(
      new RegExp(ITEMS_PER_PAGE, 'g'),
      paginationState.itemsPerPage?.toString()
    )
    .replace(new RegExp(TOTAL_ITEMS, 'g'), totalItems?.toString());
};
